import { AuditEventType, AuditTrailFilters } from '../../../../../backend/src/auditTrail/auditTrail.types';
import {
  FilterField,
  ServerSideFilterPopover as GenericServerSideFilterPopover,
} from '@/new/components/server-side-filter-popover';

/** Human-readable label overrides for audit event types. Falls back to a humanized form of the raw value. */
const AUDIT_EVENT_LABEL_OVERRIDES: Partial<Record<AuditEventType, string>> = {
  [AuditEventType.MatterCreate]: 'Created Matter',
  [AuditEventType.MatterEdit]: 'Edited Matter',
  [AuditEventType.MatterDelete]: 'Deleted Matter',
  [AuditEventType.MatterAddUsers]: 'Added User to Matter',
  [AuditEventType.MatterRemoveUsers]: 'Removed User from Matter',
  [AuditEventType.MatterAddIssue]: 'Added Issue to Matter',
  [AuditEventType.MatterRemoveIssue]: 'Removed Issue from Matter',
  [AuditEventType.MatterAddParty]: 'Added Party to Matter',
  [AuditEventType.MatterRemoveParty]: 'Removed Party from Matter',
  [AuditEventType.MatterUpdateAdditionalContext]: 'Updated Matter Additional Context',
  [AuditEventType.MatterTogglePermissionOverrides]: 'Toggled Matter Permission Overrides',
  [AuditEventType.MatterSetPermissionOverride]: 'Set Matter Permission Override',
  [AuditEventType.MatterRemovePermissionOverride]: 'Removed Matter Permission Override',
  [AuditEventType.MatterResetPermissionOverrides]: 'Reset Matter Permission Overrides',
  [AuditEventType.SubsetCreate]: 'Created Subset',
  [AuditEventType.SubsetEdit]: 'Edited Subset',
  [AuditEventType.SubsetDelete]: 'Deleted Subset',
  [AuditEventType.SubsetAddIssue]: 'Added Issue to Subset',
  [AuditEventType.SubsetRemoveIssue]: 'Removed Issue from Subset',
  [AuditEventType.RunCreate]: 'Created Run',
  [AuditEventType.RunReprocess]: 'Reprocessed Run',
  [AuditEventType.DocumentUpload]: 'Uploaded Documents',
  [AuditEventType.DocumentDelete]: 'Deleted Document',
  [AuditEventType.DocumentDownload]: 'Downloaded Documents',
  [AuditEventType.FactCreate]: 'Created Fact',
  [AuditEventType.FactUpdate]: 'Updated Fact',
  [AuditEventType.FactBulkUpdate]: 'Bulk Updated Facts',
  [AuditEventType.FactDelete]: 'Archived Fact',
  [AuditEventType.FactBulkDelete]: 'Bulk Deleted Facts',
  [AuditEventType.FactDownload]: 'Downloaded Facts',
  [AuditEventType.ChronologyCreate]: 'Created Chronology',
  [AuditEventType.ChronologyEdit]: 'Edited Chronology',
  [AuditEventType.ChronologyDelete]: 'Deleted Chronology',
  [AuditEventType.ChronologyAddFact]: 'Added Fact to Chronology',
  [AuditEventType.ChronologyVerifyFact]: 'Verified Fact in Chronology',
  [AuditEventType.ChronologyRemoveFact]: 'Removed Fact from Chronology',
  [AuditEventType.ChronologyDownload]: 'Downloaded Chronology',
  [AuditEventType.DocChronologyCreate]: 'Created Doc Chronology',
  [AuditEventType.DocChronologyEdit]: 'Edited Doc Chronology',
  [AuditEventType.DocChronologyDelete]: 'Deleted Doc Chronology',
  [AuditEventType.DocChronologyUpdateDoc]: 'Updated Doc Chronology Document',
  [AuditEventType.DocChronologyAddFact]: 'Added Fact to Doc Chronology',
  [AuditEventType.DocChronologyRemoveFact]: 'Removed Fact from Doc Chronology',
  [AuditEventType.DocChronologyAddFactToChronology]: 'Added Fact to Doc Chronology',
  [AuditEventType.DocChronologyAddDocument]: 'Added Document to Doc Chronology',
  [AuditEventType.DocChronologyRemoveDocument]: 'Removed Document from Doc Chronology',
  [AuditEventType.DocChronologyAddDocs]: 'Added Docs to Doc Chronology',
  [AuditEventType.DocChronologyDownload]: 'Downloaded Document Chronology',
  [AuditEventType.CommentAdd]: 'Added Comment',
  [AuditEventType.CommentUpdate]: 'Updated Comment',
  [AuditEventType.CommentDelete]: 'Deleted Comment',
  [AuditEventType.ThreadCreate]: 'Created Kim Thread (Legacy)',
  [AuditEventType.ThreadDelete]: 'Deleted Kim Thread (Legacy)',
  [AuditEventType.ThreadDownload]: 'Downloaded Kim Answer',
  [AuditEventType.KimCreateThread]: 'Created Kim Thread',
  [AuditEventType.KimDeleteThread]: 'Deleted Kim Thread',
  [AuditEventType.RealtimeUse]: 'Started Realtime Session',
  [AuditEventType.InconsistencyDelete]: 'Deleted Inconsistency',
  [AuditEventType.InconsistencyEdit]: 'Edited Inconsistency',
  [AuditEventType.InconsistencyDownload]: 'Downloaded Inconsistencies',
  [AuditEventType.InconsistencyReportCreate]: 'Created Inconsistency Report',
  [AuditEventType.InconsistencyReportDelete]: 'Deleted Inconsistency Report',
  [AuditEventType.PersonUpdate]: 'Updated Entity',
  [AuditEventType.PersonDelete]: 'Deleted Entity',
  [AuditEventType.PersonDownload]: 'Downloaded Entities',
  [AuditEventType.RelativitySync]: 'Synced Relativity Metadata',
  [AuditEventType.RelativityBuildParentChildRelationships]: 'Built Relativity Parent/Child Relationships',
  [AuditEventType.ProceduralTimelineEdit]: 'Edited Procedural Timeline',
  [AuditEventType.ProceduralTimelineFileUpload]: 'Uploaded Procedural Timeline File',
  [AuditEventType.ProceduralTimelineSuggestionAccept]: 'Accepted Procedural Timeline Suggestion',
  [AuditEventType.ProceduralTimelineSuggestionReject]: 'Rejected Procedural Timeline Suggestion',
  [AuditEventType.ProceduralTimelineEventCreate]: 'Created Procedural Timeline Event',
  [AuditEventType.ProceduralTimelineEventUpdate]: 'Updated Procedural Timeline Event',
  [AuditEventType.ProceduralTimelineEventDelete]: 'Deleted Procedural Timeline Event',
};

/**
 * Convert a raw `resource:action` audit event string into a human-readable label.
 * Falls back to humanizing the raw value so newly-added events get a reasonable default
 * even before being added to the override map.
 */
export function formatAuditEventType(eventType: string): string {
  const override = AUDIT_EVENT_LABEL_OVERRIDES[eventType as AuditEventType];
  if (override) return override;

  // Fallback: "fooBar:do-thing" → "Foo Bar Do Thing"
  return eventType
    .replace(/:/g, ' ')
    .replace(/-/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const auditEventOptions = Object.values(AuditEventType)
  .map((value) => ({ value, label: formatAuditEventType(value) }))
  .sort((a, b) => a.label.localeCompare(b.label));

const filterFields: FilterField[] = [
  {
    type: 'multiSelect',
    key: 'auditEventType',
    label: 'Audit Event',
    placeholder: 'Select event types...',
    options: auditEventOptions,
  },
  {
    type: 'dateRange',
    startKey: 'startDate',
    endKey: 'endDate',
    label: 'Event Date Range',
  },
];

interface AuditTrailFilterPopoverProps {
  filters: AuditTrailFilters;
  onFiltersChange: (filters: AuditTrailFilters) => void;
  clearAllFilters: () => void;
}

export function AuditTrailFilterPopover({ filters, onFiltersChange, clearAllFilters }: AuditTrailFilterPopoverProps) {
  return (
    <GenericServerSideFilterPopover
      filters={filters}
      onFiltersChange={onFiltersChange}
      clearAllFilters={clearAllFilters}
      fields={filterFields}
    />
  );
}
